import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";
import { offeredDepartmentId, practiceFeatures } from "../config/practice";

/*
 * The MedTrack board's data (rooms' occupants, waiting room, tele-health).
 *
 * 9928: GET /medtrack/get (one row per patient; ?departmentid= for a
 *       per-department board).
 * 7825 (practiceFeatures().osaBoard): GET /medtrack/fetch, the route OSA's
 *       own board polls -- the same rows without the per-patient grouping (so
 *       a shared room returns every occupant), plus `payInCash`; it takes no
 *       department filter.
 * Rooms: GET /medtrack/get_rooms (+ the same ?departmentid=).
 *
 * All three are served by v2 (2026-11-01, retiring v1): MEDTRACK_V2 sends them
 * to the v2 gateway whatever the client-routing rules say. The token is sent
 * exactly as before (api.js: the stored login when there is one, nothing on
 * the Odessa TV); v2 answers a caller with no token, or a stale one, with
 * board-only rows (no DOB/phone/email/notes, names cut to "Last4, F"), like v1
 * did. They are marked as a v2-routed read (`_gw: "v2"`, `_gwRead`), so api.js
 * retries the same call once on v1 when v2 has no such route (bare FastAPI 404)
 * or gives no answer (restart / 502-504 / the timeout below) -- the wall TV
 * keeps working through a v2 restart. Those fallbacks show up in v1's access
 * log with `_gwfb=` in the query string.
 *
 * `config` is the board's existing request config, passed through unchanged.
 */
export const MEDTRACK_V2 = {
  baseURL: V2_BASE_URL,
  _gw: "v2",
  _gwRead: true,
  timeout: 20000,
  _gwTimeoutSet: true,
};

// The board's department query string from the page's own ?dept= (1 Odessa,
// 2 Frisco, 3 CODE on 9928): "?departmentid=<n>" for a department the
// practice lists, "" (every department) otherwise.
export const medtrackDepartmentQS = (search) => {
  let raw = null;
  try {
    raw = new URLSearchParams(search || "").get("dept");
  } catch (e) {
    raw = null;
  }
  const id = offeredDepartmentId(raw);
  return id ? `?departmentid=${id}` : "";
};

export const getMedtrackBoard = (practiseid, deptQS, config) =>
  practiceFeatures().osaBoard
    ? api.get(`/v1/${practiseid}/medtrack/fetch`, { ...config, ...MEDTRACK_V2 })
    : api.get(`/v1/${practiseid}/medtrack/get${deptQS}`, { ...config, ...MEDTRACK_V2 });

// The board's rooms (id + room name are all it reads).
export const getMedtrackRooms = (practiseid, deptQS) =>
  api.get(`/v1/${practiseid}/medtrack/get_rooms${deptQS}`, { ...MEDTRACK_V2 });

// Today's visits in `roomName` (room_no trimmed), one per patient (a patient
// can have more than one visit row), in the order the server sent them.
export const roomOccupants = (rows, roomName) => {
  const inRoom = (rows || []).filter(
    (apt) => apt.room_no !== null && apt.room_no !== undefined && apt.room_no !== "" &&
      String(apt.room_no).trim() === roomName
  );
  return inRoom.filter(
    (a, i) => inRoom.findIndex((b) => String(b.patientid) === String(a.patientid)) === i
  );
};

// "Last, F" as the board abbreviates a name (care's tiles: 3 letters of the
// surname; a board profile's surnameLetters -- OSA: 4 -- when passed).
export const boardName = (p, surnameLetters = 3) =>
  `${p?.lastname?.substring(0, surnameLetters) || ""}, ${p?.firstname?.charAt(0) || ""}`;

// Visits in one status list (profile.medtrack.statusPanels): rows of `data`
// whose visit_confirmation is exactly `status`.
export const statusPanelRows = (rows, status) =>
  (rows || []).filter((apt) => apt.visit_confirmation === status);

// The Pay-in-Cash list. medtrack/fetch names it `payInCash`; OSA's build
// also accepted the other spellings, kept here for a server that sends them.
export const payInCashRows = (data) => {
  const raw = data?.payInCash ?? data?.payincash ?? data?.pay_in_cash ?? data?.payIncash;
  return Array.isArray(raw) ? raw : [];
};
